import { ImageContext, ImageContextForAdding, ImageHandlingDisplayMode } from '@v2/components/imageHandling/types/types.d';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getThumbnailImagePosition, IMAGE_HANDLING_HEADER_HEIGHT, KEYS_TAGS } from '@components/imageHandling/common';
import { ScrollView, StatusBar, StyleProp, useWindowDimensions, ViewStyle } from 'react-native';
import { isAndroid, isPhoneNative, isWeb, navigationRef } from '@helpers';
import { ColorFunction } from '@styles/colors/types';
import { fullHeight, scale } from '@components/frame';
import { generateGropedImagesAndKeys } from '@v2/components/imageHandling/helpers';
import { useImageHandlingChecks } from './useImageHandlingChecks';
import { isImageContextHasScopeId, selectPreImageHandlingScreen } from '@reducers/v2/imageHandlingV2/imageHandlingV2.slice';
import { useImageHandlingViewModel } from '@v2/components/imageHandling/viewmodels/useImageHandlingViewModel';
import { useSelector } from 'react-redux';
import { EXPANDABLE_IMAGE_SCREEN_PATTERNS } from '@v2/components/imageHandling/constants';
import {
  ANNOTATION_LIST_DEFAULT_WIDTH,
  DRAWING_BOTTOM_BAR_HEIGHT,
  DRAWING_TOP_BAR_HEIGHT,
  MOBILE_ANNOTATION_LIST_HEADER_HEIGHT,
} from '../components/DrawingCanvas/constants';
import { SAFE_TOP_INSET } from '@v2/components/imageHandling/constants';

interface UseManageImageHandlingParams {
  C: ColorFunction;
}

export const useManageImageHandling = ({ C }: UseManageImageHandlingParams) => {
  const { width, height } = useWindowDimensions();
  const route = navigationRef?.current?.getCurrentRoute();

  const preImageHandlingScreen = useSelector(selectPreImageHandlingScreen);

  const thumbnailScrollRef = useRef<ScrollView>(null);

  const params = route?.params;

  const initialMode = params?.imageHandlingMode ?? ImageHandlingDisplayMode.PICTURE;

  const [mode, setMode] = useState<ImageHandlingDisplayMode>(initialMode);

  const initialShowActionsHideState = params?.showActions;

  const [showActions, setShowActions] = useState<boolean>(initialShowActionsHideState);
  const triggerShowActions = () => setShowActions((prev) => !prev);

  const [isDrawing, setIsDrawing] = useState<boolean>(false);

  const triggerDraw = useCallback(() => {
    setIsDrawing((prev) => {
      const next = !prev;
      // On native, hide all overlay actions when entering draw mode so the image gets the full screen.
      // Existing `!showActions` checks throughout the tree pick this up automatically.
      if (next && !isWeb) setShowActions(false);
      return next;
    });
  }, []);
  const exitDrawing = useCallback(() => setIsDrawing(false), []);

  const imageContext: ImageContext = useMemo(() => {
    const documentId = params?.documentId;
    if (documentId) {
      return {
        documentId,
      };
    }

    if (params?.p) {
      return {
        scopeId: KEYS_TAGS.Part,
        scopeReferenceId: +params?.p,
      };
    }

    if (params?.r) {
      return { scopeId: KEYS_TAGS.Recipe, scopeReferenceId: +params?.r };
    }

    if (params?.rOp) {
      return { scopeId: KEYS_TAGS.RecipeOperation, scopeReferenceId: +params?.rOp };
    }

    if (params?.c) {
      return { scopeId: KEYS_TAGS.Clamping, scopeReferenceId: +params?.c };
    }

    if (params?.b) {
      return { scopeId: KEYS_TAGS.Batch, scopeReferenceId: +params?.b };
    }

    if (params?.pO) {
      return { scopeId: KEYS_TAGS.ProductionOrder, scopeReferenceId: +params?.pO };
    }

    if (params?.pOp) {
      return { scopeId: KEYS_TAGS.ProductionOperation, scopeReferenceId: +params?.pOp };
    }

    if (params?.t) {
      return { scopeId: KEYS_TAGS.Tool, scopeReferenceId: +params?.t };
    }

    if (params?.m) {
      return { scopeId: KEYS_TAGS.Machine, scopeReferenceId: +params?.m };
    }

    return {};
  }, [params]);

  const imageContextHasScopeId = isImageContextHasScopeId(imageContext);
  const imageContextForAdding: ImageContextForAdding = imageContextHasScopeId ? { tags: [imageContext] } : {};

  const {
    state: { imagesDocuments, loading, selectedIndex, setSelectedIndex },
    actions: { loadImagesDocuments, cropImageDocument, rotateImageDocument, removeImageDocument },
  } = useImageHandlingViewModel({ imageContext });

  useEffect(() => {
    loadImagesDocuments();
  }, []);

  const mainContainerStyles: StyleProp<ViewStyle> = { width, height, position: 'absolute' };

  const positions = {
    picture: 0,
    thumbnail: 0,
  };

  switch (mode) {
    case 'picture':
      positions.picture = 1000;
      if (isAndroid) {
        StatusBar.setBackgroundColor(C('Background/Light/menuApp'));
      }
      break;
    case 'thumbnail':
      positions.thumbnail = 1000;
      if (isAndroid) {
        StatusBar.setBackgroundColor(C('Background/Light/System'));
      }
      break;
  }

  const { isSingleImage } = useImageHandlingChecks({ images: imagesDocuments });

  const { groupedImages, groupedImagesKeys } = generateGropedImagesAndKeys(imagesDocuments);

  const selectedIndexChanged = () => {
    setTimeout(() => {
      const offset = getThumbnailImagePosition({ images: imagesDocuments, selectedIndex, groupedImages, groupedImagesKeys });
      if (thumbnailScrollRef && thumbnailScrollRef.current) {
        thumbnailScrollRef.current.scrollTo({ y: offset, animated: false });
      }
    }, 1000);
  };

  const onImagePress = () => {
    triggerShowActions();
  };

  const preImageHandlingScreenName = preImageHandlingScreen?.name as string | undefined;

  const shouldDisplayCollapseIcon =
    !!preImageHandlingScreenName && EXPANDABLE_IMAGE_SCREEN_PATTERNS.some((pattern) => preImageHandlingScreenName.includes(pattern));

  // ImageHandlingHeader stays visible during drawing on web; on native we hide it so DrawingTopBar
  // can sit at the very top (with only notchTop) and the image gets the full screen height.
  const shouldDisplayHeader = isWeb || (showActions && !isDrawing);
  const shouldDisplayBottomComponent = showActions && isPhoneNative && !isDrawing;

  // Total vertical inset above the image while drawing — keeps the regular header on web,
  // collapses to the shared `SAFE_TOP_INSET` on native (notch with platform fallback).
  const drawingTopInset = isWeb ? scale(IMAGE_HANDLING_HEADER_HEIGHT) : SAFE_TOP_INSET;
  // Bottom inset matches `useManageDrawingCanvasLayout.surfaceBottomInset` — phones reserve the
  // collapsible strip header above the toolbar; the expanded body floats over the image.
  const drawingBottomInset = scale(DRAWING_BOTTOM_BAR_HEIGHT) + (isPhoneNative ? scale(MOBILE_ANNOTATION_LIST_HEADER_HEIGHT) : 0);
  // Right inset — phones drop the desktop sidebar entirely.
  const drawingRightInset = isPhoneNative ? 0 : scale(ANNOTATION_LIST_DEFAULT_WIDTH);

  // Picture layout — during drawing the image is inset by the header + floating bar / list sizes so the full image is annotatable.
  const pictureContainerStyle: StyleProp<ViewStyle> = isDrawing
    ? {
        marginTop: drawingTopInset + scale(DRAWING_TOP_BAR_HEIGHT),
        marginRight: drawingRightInset,
        marginBottom: drawingBottomInset,
      }
    : isWeb
    ? { marginTop: scale(IMAGE_HANDLING_HEADER_HEIGHT) }
    : {};

  const pictureHeight = isDrawing
    ? fullHeight - drawingTopInset - scale(DRAWING_TOP_BAR_HEIGHT) - drawingBottomInset
    : isWeb
    ? fullHeight - scale(IMAGE_HANDLING_HEADER_HEIGHT)
    : undefined;

  const pictureWidth = isDrawing ? width - drawingRightInset : undefined;

  return {
    mode,
    setMode,
    imagesDocuments,
    imagesDocumentsLoading: loading,
    selectedIndex,
    setSelectedIndex,
    loadImagesDocuments,
    cropImageDocument,
    rotateImageDocument,
    removeImageDocument,
    mainContainerStyles,
    isSingleImage,
    positions,
    imageContext,
    imageContextForAdding,
    showActions,
    setShowActions,
    triggerShowActions,
    groupedImages,
    groupedImagesKeys,
    thumbnailScrollRef,
    selectedIndexChanged,
    onImagePress,
    shouldDisplayCollapseIcon,
    isDrawing,
    triggerDraw,
    exitDrawing,
    shouldDisplayHeader,
    shouldDisplayBottomComponent,
    pictureContainerStyle,
    pictureHeight,
    pictureWidth,
  };
};
